import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Target, ArrowRight } from "lucide-react";
import { api, statusLabel } from "@/lib/api";

const parseDate = (d) => {
  if (!d) return 0;
  const dm = /^(\d{2})-(\d{2})-(\d{4})$/.exec(d);
  if (dm) return new Date(`${dm[3]}-${dm[2]}-${dm[1]}`).getTime();
  const t = new Date(d).getTime();
  return isNaN(t) ? 0 : t;
};

const TournamentCard = ({ t }) => {
  const isFinale = t.name === "Finale";
  return (
    <Link
      to={`/tournaments/${t.id}`}
      data-testid={`tournament-list-${t.number}`}
      className={`group bg-white border p-6 flex items-center justify-between transition-colors ${isFinale ? "border-[#D4AF37]" : "border-[#E5E5E5] hover:border-[#D4AF37]"}`}
    >
      <div className="flex items-center gap-5">
        <span className="font-heading text-4xl font-black text-[#E5E5E5] group-hover:text-[#D4AF37] transition-colors">
          {isFinale ? "F" : String(t.number).padStart(2, "0")}
        </span>
        <div>
          <h3 className="font-heading font-semibold text-lg flex items-center gap-2 flex-wrap">
            {t.name}
            {t.is_doubles && <span className="text-[9px] uppercase tracking-wide bg-[#D4AF37] text-[#0A0A0A] px-1.5 py-0.5 font-bold">Koppel</span>}
            {t.counts_for_ranking === false && <span className="text-[9px] uppercase tracking-wide bg-[#525252] text-white px-1.5 py-0.5 font-bold">Telt niet mee</span>}
          </h3>
          <div className="mt-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[#525252]">
            <span className={`w-2 h-2 rounded-full ${t.status === "completed" ? "bg-[#16A34A]" : t.status === "live" ? "bg-[#D4AF37]" : "bg-[#A0A0A0]"}`} />
            {statusLabel(t.status)}
            {t.date ? ` · ${t.date}` : ""}{t.time ? ` · ${t.time}` : ""}
          </div>
        </div>
      </div>
      <ArrowRight className="w-5 h-5 text-[#525252] group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all" />
    </Link>
  );
};

export default function Tournaments() {
  const [tours, setTours] = useState([]);

  useEffect(() => {
    api.get("/tournaments").then(({ data }) => setTours(data));
  }, []);

  const upcoming = tours
    .filter((t) => t.status !== "completed")
    .sort((a, b) => parseDate(a.date) - parseDate(b.date));
  const completed = tours
    .filter((t) => t.status === "completed")
    .sort((a, b) => parseDate(b.date) - parseDate(a.date));

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 lg:py-16">
      <div className="flex items-center gap-3 mb-2">
        <Target className="w-7 h-7 text-[#D4AF37]" />
        <h1 className="font-heading text-4xl lg:text-5xl tracking-tighter font-black">Toernooien</h1>
      </div>
      <p className="text-[#525252] mb-10">Alle toernooien van het seizoen. Klik op een toernooi voor de volledige resultaten.</p>

      {upcoming.length > 0 && (
        <section className="mb-12" data-testid="upcoming-section">
          <h2 className="font-heading text-sm uppercase tracking-[0.2em] font-bold text-[#525252] mb-4">Aankomende toernooien</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {upcoming.map((t) => <TournamentCard key={t.id} t={t} />)}
          </div>
        </section>
      )}

      {completed.length > 0 && (
        <section data-testid="completed-section">
          <h2 className="font-heading text-sm uppercase tracking-[0.2em] font-bold text-[#525252] mb-4">Afgeronde toernooien</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {completed.map((t) => <TournamentCard key={t.id} t={t} />)}
          </div>
        </section>
      )}

      {tours.length === 0 && (
        <p className="text-[#525252]" data-testid="no-tournaments">Nog geen toernooien beschikbaar.</p>
      )}
    </div>
  );
}
